import React from 'react';
import FloatingParticles from '../components/layout/FloatingParticles';
import FeatureCard from '../components/landing/FeatureCard';
import RotatingCarousel from '../components/landing/RotatingCarousel';
import PricingSection from '../components/landing/PricingSection';

interface HomePageProps {
  navigateToLogin: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ navigateToLogin }) => {
  const features = [
    {
      gifSrc: "/images/feature card gifs/ad.gif",
      title: "Flawless Auto Detection",
      description: "We feature flawless auto detection for all weapons, scopes, skins, resolutions & ui scales"
    },
    {
      gifSrc: "/images/feature card gifs/undetected.gif",
      title: "Undetected",
      description: "We are currently undetected. In addition, you are generated a completely unique build daily"
    },
    {
      gifSrc: "/images/feature card gifs/recoil.gif",
      title: "Unmatched recoil control",
      description: "Perfect microsecond timing to ensure the best and most consistent spray possible"
    },
    {
      gifSrc: "/images/feature card gifs/scriptcheck.gif",
      title: "Immune to script checkers",
      description: "Completely immune to client side script checking tools"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white overflow-hidden">
      <FloatingParticles />

      {/* Hero Section */}
      <div className="pt-20 pb-2 px-4 relative">
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-300 via-blue-400 to-blue-200 bg-clip-text text-transparent leading-tight py-2">
            Stop using Inferior scripts
          </h1>
          <div className="relative inline-block">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-blue-400/20 rounded-full blur-lg"></div>
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/10 to-blue-400/10 rounded-full blur-xl"></div>
            <div className="inline-flex items-center gap-6 px-6 py-3 rounded-full bg-gray-800/50 backdrop-blur-sm border border-blue-400/20 relative">
              <div className="flex items-center text-sm text-blue-300/90">
                <span className="w-1.5 h-1.5 bg-blue-400/80 rounded-full mr-2"></span>
                Custom builds
              </div>
              <div className="flex items-center text-sm text-blue-300/90">
                <span className="w-1.5 h-1.5 bg-blue-400/80 rounded-full mr-2"></span>
                Real-time Updates
              </div>
              <div className="flex items-center text-sm text-blue-300/90">
                <span className="w-1.5 h-1.5 bg-blue-400/80 rounded-full mr-2"></span>
                24/7 Support
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rotating Carousel Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full h-[21.6rem] relative select-none mx-auto max-w-4xl" style={{ perspective: '2400px' }}>
          <RotatingCarousel />
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`h-full relative ${
                index === 2 ? 'flex justify-end' : // Bottom left - align right
                index === 3 ? 'flex justify-start' : // Bottom right - align left
                ''
              }`}
            >
              <div className={`${
                index === 2 ? 'w-[85%] transform origin-right h-[90%]' : // Bottom left - scale from right
                index === 3 ? 'w-[85%] transform origin-left h-[90%]' : // Bottom right - scale from left
                'w-full'
              }`}>
                <FeatureCard {...feature} isSmaller={index >= 2} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Info with Pricing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="inline-block text-3xl font-bold bg-gradient-to-r from-blue-300 via-blue-400 to-blue-200 bg-clip-text text-transparent leading-tight mb-4 relative">
            Dominate Every Game
            <div className="absolute -bottom-1 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Discover our collection of premium game hacks, including undetected cheats and high-quality modules for popular games like Rust, Apex, and more.
          </p>
        </div>
        
        <PricingSection />
      </div>
      
      {/* Footer with copyright and legal links */}
      <footer className="border-t border-gray-800/50 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm mb-4 md:mb-0">
              &copy; recoil.club 2025. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <button className="text-gray-500 text-sm hover:text-blue-400 transition-colors">
                Terms of Service
              </button>
              <button className="text-gray-500 text-sm hover:text-blue-400 transition-colors">
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 